import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InvertColorsOff, InvertColors } from '@material-ui/icons';
import { Search, Settings } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import {
    Box,
    Tooltip,
    CardContent,
    IconButton,
    TextField,
    InputAdornment,
    SvgIcon,
    makeStyles,
    Theme,
} from '@material-ui/core';
import { IFiltersHeader } from './types';

const useStyles = makeStyles((theme: Theme) => ({
    searchBar: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
        [theme.breakpoints.up('sm')]: {
            width: 400,
        },
    },
    searchInput: {
        borderRadius: theme.spacing(2),
    },
    cardContent: {
        display: 'flex',
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row-reverse',
        },
        [theme.breakpoints.down('xs')]: {
            display: 'block',
            gap: 100,
        },
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    buttonsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 20,
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'space-between',
        },
    },
}));

/**
 * Represents the header of the filters section of the table list view.
 *
 * @return {*}
 */
export const FiltersHeader = <TField,>({
    temperatures = {
        isTemperaturesVisible: false,
        setVisibleTemperatures: () => {},
        isEnabled: false,
        localStorageVisibleRatings: '',
    },
    advancedSearch = {
        setAdvancedSearchOpen: () => {},
        isEnabled: false,
    },
    queryField = {
        searchLabel: '',
        isEnabled: false,
    },
    create = {
        createLabel: '',
        createPath: '',
        icon: null,
    },
    setModalRowOpen,
    loadInformation = {
        loadInformation: () => {},
        isEnabled: false,
    },
    setValues,
    values,
    setQueryLocal = () => {},
    autoFetch = true,
}: IFiltersHeader<TField>) => {
    const { t } = useTranslation();
    const history = useHistory();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const classes = useStyles();

    const labelSearch: string = t(queryField.searchLabel);
    const labelCreate: string = t(create.createLabel);
    const labelAdvancedSearch: string = t('Buttons.AdvancedSearch');
    const labelHighlight: string = t('Leads.Highlight');
    const labelShowRows: string = t('Buttons.ShowRows');
    const labelReload: string = t('Buttons.Reload');

    const handleAdvancedSearch = () =>
        advancedSearch.setAdvancedSearchOpen(true);

    const handleModalRowOpen = () => {
        if (setModalRowOpen) setModalRowOpen(true);
    };

    const handleTemperatures = () => {
        if (!temperatures?.isEnabled) return;
        const newVisibleTemperatures = !temperatures.isTemperaturesVisible;

        temperatures.setVisibleTemperatures(newVisibleTemperatures);
        if (!newVisibleTemperatures)
            localStorage.removeItem(temperatures.localStorageVisibleRatings);
        else
            localStorage.setItem(
                temperatures.localStorageVisibleRatings,
                String(newVisibleTemperatures)
            );
    };

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        setQuery(event.target.value);

    const handleKeyUp = async (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key !== 'Enter') return;
        const newQuery = inputRef.current?.value ?? '';
        if (autoFetch === false && setQueryLocal) {
            setQueryLocal(newQuery);
        }
        setValues({
            ...values,
            query: newQuery,
            page: 0,
            isQueryEnabled: Boolean(newQuery),
        });
    };

    useEffect(() => {
        setQuery(values.query || '');
    }, [values.query]);

    return (
        <CardContent className={classes.cardContent}>
            <Box className={classes.buttonsContainer}>
                {advancedSearch.isEnabled && (
                    <Tooltip title={labelAdvancedSearch}>
                        <IconButton
                            onClick={handleAdvancedSearch}
                            data-testid="advanced-search-button"
                        >
                            <Search />
                        </IconButton>
                    </Tooltip>
                )}
                {create.createLabel && create.createPath && (
                    <Tooltip title={labelCreate}>
                        <IconButton
                            onClick={() => history.push(create.createPath)}
                            data-testid="create-button"
                        >
                            {create.icon}
                        </IconButton>
                    </Tooltip>
                )}
                {temperatures?.isEnabled && (
                    <Tooltip title={labelHighlight}>
                        <IconButton
                            onClick={handleTemperatures}
                            data-testid="highlight-button"
                        >
                            {temperatures.isTemperaturesVisible ? (
                                <InvertColors />
                            ) : (
                                <InvertColorsOff />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
                {setModalRowOpen && (
                    <Tooltip title={labelShowRows}>
                        <IconButton
                            onClick={handleModalRowOpen}
                            data-testid="show-rows-button"
                        >
                            <Settings />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={labelReload}>
                    <IconButton
                        onClick={() =>
                            loadInformation.isEnabled &&
                            loadInformation.loadInformation()
                        }
                        data-testid="reload-button"
                    >
                        <FontAwesomeIcon
                            icon={'sync'}
                            spin={!loadInformation.isEnabled}
                            style={{
                                fontSize: 21,
                            }}
                        />
                    </IconButton>
                </Tooltip>
            </Box>
            <TextField
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SvgIcon fontSize="small" color="action">
                                <Search />
                            </SvgIcon>
                        </InputAdornment>
                    ),
                    className: classes.searchInput,
                }}
                inputProps={{
                    'data-testid': 'search-input',
                }}
                onKeyUp={handleKeyUp}
                placeholder={labelSearch}
                variant="outlined"
                disabled={!queryField.isEnabled}
                inputRef={inputRef}
                value={query}
                onChange={handleQueryChange}
                className={classes.searchBar}
            />
        </CardContent>
    );
};
